import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import { FadeInUp } from '../components/Animations';

/* ────── Animated counter ────── */
function Counter({ end, suffix = '', duration = 2000 }) {
  const ref = useRef(null);
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setStarted(true); obs.unobserve(el); } },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!started) return;
    const steps = 60;
    const increment = end / steps;
    let current = 0;
    const interval = setInterval(() => {
      current += increment;
      if (current >= end) { setCount(end); clearInterval(interval); }
      else setCount(Math.floor(current));
    }, duration / steps);
    return () => clearInterval(interval);
  }, [started, end, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
}

export default function Home() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  /* ────── Typing effect ────── */
  const words = t('hero.typingWords', { returnObjects: true });
  const [wordIndex, setWordIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [deleting, setDeleting] = useState(false);
  const [displayed, setDisplayed] = useState('');

  const tick = useCallback(() => {
    const current = words[wordIndex] || '';
    if (!deleting) {
      setDisplayed(current.slice(0, charIndex + 1));
      setCharIndex((c) => c + 1);
      if (charIndex + 1 >= current.length) {
        setTimeout(() => setDeleting(true), 1600);
        return;
      }
    } else {
      setDisplayed(current.slice(0, charIndex - 1));
      setCharIndex((c) => c - 1);
      if (charIndex - 1 <= 0) {
        setDeleting(false);
        setWordIndex((w) => (w + 1) % words.length);
      }
    }
  }, [charIndex, deleting, wordIndex, words]);

  useEffect(() => {
    const speed = deleting ? 50 : 120;
    const timer = setTimeout(tick, speed);
    return () => clearTimeout(timer);
  }, [tick, deleting]);

  /* ────── Client logo placeholders ────── */
  const clientIcons = [
    'fas fa-building', 'fas fa-store', 'fas fa-industry',
    'fas fa-landmark', 'fas fa-hotel', 'fas fa-hospital',
    'fas fa-graduation-cap', 'fas fa-utensils',
  ];

  return (
    <>
      <SEO
        title={isRTL ? 'وجود لينك | وكالة تسويق رقمي' : 'Wojod Link | Digital Marketing Agency'}
        description={t('hero.description')}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'Wojod Link',
          alternateName: 'وجود لينك',
          url: 'https://wojodlink.com',
          logo: 'https://wojodlink.com/og-image.png',
          sameAs: [],
          contactPoint: {
            '@type': 'ContactPoint',
            contactType: 'customer service',
            availableLanguage: ['Arabic', 'English']
          }
        }}
      />

      {/* ── Hero ── */}
      <section className="hero">
        <div className="hero-background">
          <div className="hero-grid-pattern" />
          <div className="hero-gradient-orb orb-1" />
          <div className="hero-gradient-orb orb-2" />
          <div className="hero-gradient-orb orb-3" />
          <div className="hero-glow" />
        </div>

        <div className="hero-content">
          <FadeInUp>
            <span className="hero-badge">
              <i className="fas fa-rocket" />
              {isRTL ? 'وكالة تسويق رقمي رائدة' : 'Leading Digital Marketing Agency'}
            </span>
          </FadeInUp>

          <FadeInUp delay={0.1}>
            <h1 className="hero-title">
              {t('hero.title')}{' '}
              <span className="highlight">{t('hero.titleHighlight')}</span>
            </h1>
          </FadeInUp>

          <FadeInUp delay={0.2}>
            <div className="typing-container">
              <span className="typing-prefix">{t('hero.prefix')}</span>
              <span className="typed-text">{displayed}</span>
              <span className="typing-cursor">|</span>
            </div>
          </FadeInUp>

          <FadeInUp delay={0.3}>
            <p className="hero-description">{t('hero.description')}</p>
          </FadeInUp>

          <FadeInUp delay={0.4}>
            <div className="hero-buttons">
              <Link to="/contact" className="btn btn-primary btn-glow">
                {t('hero.cta')} <i className={`fas fa-arrow-${isRTL ? 'left' : 'right'}`} />
              </Link>
              <Link to="/about" className="btn btn-glass">
                {t('hero.ctaSecondary')}
              </Link>
            </div>
          </FadeInUp>

          <FadeInUp delay={0.55}>
            <div className="hero-stats">
              <div className="hero-stat">
                <span className="hero-stat-number"><Counter end={150} suffix="+" /></span>
                <span className="hero-stat-label">{isRTL ? 'عميل سعيد' : 'Happy Clients'}</span>
              </div>
              <div className="hero-stat-divider" />
              <div className="hero-stat">
                <span className="hero-stat-number"><Counter end={300} suffix="+" /></span>
                <span className="hero-stat-label">{isRTL ? 'مشروع مكتمل' : 'Projects Done'}</span>
              </div>
              <div className="hero-stat-divider" />
              <div className="hero-stat">
                <span className="hero-stat-number"><Counter end={5} suffix="+" /></span>
                <span className="hero-stat-label">{isRTL ? 'سنوات خبرة' : 'Years Experience'}</span>
              </div>
            </div>
          </FadeInUp>
        </div>

        <div className="hero-scroll-indicator">
          <span />
        </div>
      </section>

      {/* ── About Home ── */}
      <section className="about-home">
        <div className="container">
          <FadeInUp>
            <div className="section-header text-center">
              <span className="section-label">{t('aboutHome.label')}</span>
              <h2 className="section-title">{t('aboutHome.title')}</h2>
              <p className="section-description">{t('aboutHome.description')}</p>
            </div>
          </FadeInUp>

          <div className="about-grid">
            {[
              { key: 'vision', icon: 'fas fa-eye' },
              { key: 'mission', icon: 'fas fa-bullseye' },
              { key: 'values', icon: 'fas fa-star' },
            ].map((item, i) => (
              <FadeInUp key={item.key} delay={i * 0.12}>
                <div className="card glass-card">
                  <div className="card-icon">
                    <i className={item.icon} />
                  </div>
                  <h3 className="card-title">{t(`aboutHome.${item.key}`)}</h3>
                  <p className="card-text">{t(`aboutHome.${item.key}Text`)}</p>
                </div>
              </FadeInUp>
            ))}
          </div>

          <FadeInUp delay={0.4}>
            <div className="text-center" style={{ marginTop: 40 }}>
              <Link to="/about" className="btn btn-outline">
                {t('aboutHome.cta')} <i className={`fas fa-arrow-${isRTL ? 'left' : 'right'}`} />
              </Link>
            </div>
          </FadeInUp>
        </div>
      </section>

      {/* ── Services Home ── */}
      <section className="services-home">
        <div className="container">
          <FadeInUp>
            <div className="section-header text-center">
              <span className="section-label">{t('servicesHome.label')}</span>
              <h2 className="section-title">{t('servicesHome.title')}</h2>
              <p className="section-description">{t('servicesHome.description')}</p>
            </div>
          </FadeInUp>

          <div className="services-grid">
            {t('servicesHome.items', { returnObjects: true }).map((svc, i) => (
              <FadeInUp key={i} delay={i * 0.1}>
                <div className="service-card">
                  <div className="service-number">{String(i + 1).padStart(2, '0')}</div>
                  <div className="service-icon">
                    <i className={svc.icon} />
                  </div>
                  <h3 className="service-title">{svc.title}</h3>
                  <p className="service-description">{svc.desc}</p>
                  <Link to="/services" className="service-link">
                    {t('servicesHome.learnMore')}{' '}
                    <i className={`fas fa-arrow-${isRTL ? 'left' : 'right'}`} />
                  </Link>
                </div>
              </FadeInUp>
            ))}
          </div>

          <FadeInUp delay={0.5}>
            <div className="text-center" style={{ marginTop: 40 }}>
              <Link to="/services" className="btn btn-primary">
                {t('servicesHome.cta')} <i className={`fas fa-arrow-${isRTL ? 'left' : 'right'}`} />
              </Link>
            </div>
          </FadeInUp>
        </div>
      </section>

      {/* ── Clients ── */}
      <section className="clients">
        <div className="container">
          <FadeInUp>
            <div className="section-header text-center">
              <span className="section-label">{t('clients.label')}</span>
              <h2 className="section-title">{t('clients.title')}</h2>
              <p className="section-description">{t('clients.description')}</p>
            </div>
          </FadeInUp>

          <div className="clients-marquee">
            <div className="clients-track">
              {[...clientIcons, ...clientIcons].map((ic, i) => (
                <div key={i} className="client-logo">
                  <i className={ic} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="cta-section">
        <div className="container">
          <FadeInUp>
            <div className="cta-content">
              <span className="cta-label">{isRTL ? 'هل أنت مستعد؟' : 'Ready to Start?'}</span>
              <h2>{t('ctaSection.title')}</h2>
              <p>{t('ctaSection.description')}</p>
              <div className="cta-buttons">
                <Link to="/contact" className="btn btn-accent btn-glow">
                  {t('ctaSection.cta')} <i className={`fas fa-arrow-${isRTL ? 'left' : 'right'}`} />
                </Link>
                <Link to="/about" className="btn btn-glass">{t('ctaSection.ctaSecondary')}</Link>
              </div>
            </div>
          </FadeInUp>
        </div>
      </section>
    </>
  );
}
